import React, { useContext } from 'react';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { JobCategoryEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import Badge from '@components/ringil3/Badges/Badge';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge';
import { Ringil3BadgeRow } from '@components/ringil3/styles';
import { formatDateInTzWithLocale } from '@utils/dateWithTz';
import SvgDelete from '@icons/delete-trash.svg?react';

interface ComponentProps {
  title: string;
  jobTypeLabel: string;
  jobCategory?: JobCategoryEnum;
  createdByName?: string;
  createdAt: string;
  /** Cancel button is rendered only when a handler is passed (caller decides visibility). */
  onCancel?: () => void;
  /** Trash (archive/delete) button is rendered only when a handler is passed (caller decides visibility). */
  onArchive?: () => void;
  /**
   * Key of the page action currently in flight (RD-2954). 'cancel' / 'archive' put a spinner on their
   * own button; any non-null value greys the other one out, so only one job action runs at a time —
   * a second click or a parallel action used to come back from the BE as a 409.
   */
  busyAction?: string | null;
  /** Extra page-specific buttons rendered left of cancel/trash. */
  buttons?: React.ReactNode;
}

/** Shared header of the typed job detail pages: "Úkol {id}" + Typ/Modul/Zadal-a badges + cancel or archive button. */
const JobDetailHeader: React.FC<ComponentProps> = ({ title, jobTypeLabel, jobCategory, createdByName, createdAt, onCancel, onArchive, busyAction = null, buttons }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const busy = (key: string) => ({ loading: busyAction === key, disabled: busyAction != null && busyAction !== key });

  return (
    <>
      <div className={'flex flex-row justify-between items-center'}>
        <COHeadline variant={'h2'} title={title} />
        <div className={'flex flex-row gap-2 items-center'}>
          {buttons}
          {onCancel != null && <RingilButton mode={'outlined-rectangle'} label={tr('ExportJobDetail.cancel', 'Zrušit')} onClick={onCancel} {...busy('cancel')} />}
          {onArchive != null && <RingilButton mode={'table'} icon={<SvgDelete />} dataTestId={'job-detail-archive'} onClick={onArchive} {...busy('archive')} />}
        </div>
      </div>

      <Ringil3BadgeRow>
        <Badge label={tr('JobDetail.type', 'Typ')} value={jobTypeLabel} />
        {jobCategory != null && <Badge label={tr('JobDetail.module', 'Modul')} value={tr(`JobList.category.${jobCategory}`, `${jobCategory}`)} />}
        {createdByName != null && (
          <CreatedBadge createdAt={createdAt} createdBy={createdByName} timezoneId={loggedUser?.defaultTimezoneId} dataTestId={'job-detail-createdAt'} />
        )}
      </Ringil3BadgeRow>
    </>
  );
};

/** Muted "Položka bude automaticky smazaná {date}" line shown under terminal job states. */
export const JobExpiryNote: React.FC<{ expiresAt?: string }> = ({ expiresAt }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  if (expiresAt == null) return null;
  return (
    <span className={'text-sm text-content-secondary'}>
      {tr('JobDetail.autoDelete', 'Položka bude automaticky smazaná {{date}}', {
        date: formatDateInTzWithLocale(expiresAt, loggedUser?.defaultTimezoneId),
      })}
    </span>
  );
};

export default JobDetailHeader;
